import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { fetchWithAuth } from '@/lib/utils/request'
import { getApiUrl, getAuthApiUrl } from '@/lib/config'
import { getAuthScheme, type LoginType } from '@/lib/auth'

/**
 * Verify token against the remote auth service at agent.fkxiaoyu.com/auth.
 * All auth-related verification goes through the remote auth endpoint,
 * separated from the main API.
 */
async function verifyTokenRemote(token: string, authScheme: string): Promise<boolean> {
  const authApiUrl = await getAuthApiUrl()
  const response = await fetch(`${authApiUrl}/auth/customer/login/check`, {
    method: 'POST',
    headers: {
      'Authorization': `${authScheme} ${token}`,
      'Content-Type': 'application/json',
      'Source': 'desktop',
      'Source-App': 'agent',
    },
    body: JSON.stringify({}),
  })

  if (!response.ok) {
    return false
  }

  try {
    const data = await response.json()
    // Result<Boolean>: { code: 200, data: true } means success
    // Accept both string "200" and number 0 as success codes (backend returns string)
    const codeOk = data?.code === 0 || data?.code === 200 || data?.code === "200"
    return codeOk && data?.data === true
  } catch {
    return false
  }
}

interface AuthState {
  isAuthenticated: boolean
  token: string | null
  isLoading: boolean
  error: string | null
  lastAuthCheck: number | null
  isCheckingAuth: boolean
  hasHydrated: boolean
  authRequired: boolean | null
  loginType: LoginType
  setHasHydrated: (state: boolean) => void
  checkAuthRequired: () => Promise<boolean>
  setToken: (token: string) => Promise<boolean>
  login: (password: string) => Promise<boolean>
  logout: () => void
  checkAuth: () => Promise<boolean>
  setLoginType: (type: LoginType) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      token: null,
      isLoading: false,
      error: null,
      lastAuthCheck: null,
      isCheckingAuth: false,
      hasHydrated: false,
      authRequired: null,
      loginType: 'local', // Default to 'local'

      setLoginType: (type: LoginType) => set({ loginType: type }),

      setHasHydrated: (state: boolean) => {
        set({ hasHydrated: state })
      },

      checkAuthRequired: async () => {
        try {
          // 远端登录，一直要保存token
          if (get().loginType === 'remote') {
            return true
          }

          const apiUrl = await getApiUrl()
          const response = await fetchWithAuth(`${apiUrl}/agent-api/auth/status`, {
            cache: 'no-store',
          })

          if (!response.ok) {
            throw new Error(`Auth status check failed: ${response.status}`)
          }

          const data = await response.json()
          const required = data.auth_enabled || false
          set({ authRequired: required })

          // If auth is not required, mark as authenticated
          if (!required) {
            set({ isAuthenticated: true, token: 'not-required' })
          }

          return required
        } catch (error) {
          console.error('Failed to check auth status:', error)

          // If it's a network error, set a more helpful error message
          if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
            set({
              error: 'Unable to connect to server. Please check if the API is running.',
              authRequired: null  // Don't assume auth is required if we can't connect
            })
          } else {
            // For other errors, default to requiring auth to be safe
            set({ authRequired: true })
          }

          // Re-throw the error so the UI can handle it
          throw error
        }
      },

      setToken: async (token: string) => {
          set({
              isAuthenticated: true,
              token: token,
              isLoading: false,
              lastAuthCheck: Date.now(),
              error: null
          })
          return true
      },

      login: async (password: string) => {
        set({ isLoading: true, error: null })
        const loginType = get().loginType
        const authScheme = getAuthScheme(loginType)
        try {
          // Verify token via remote auth endpoint (agent.fkxiaoyu.com/auth)
          const valid = await verifyTokenRemote(password, authScheme)

          if (valid) {
            set({
              isAuthenticated: true,
              token: password,
              isLoading: false,
              lastAuthCheck: Date.now(),
              error: null
            })
            return true
          } else {
            const errorMessage = 'Invalid password. Please try again.'

            set({
              error: errorMessage,
              isLoading: false,
              isAuthenticated: false,
              token: null
            })
            return false
          }
        } catch (error) {
          console.error('Network error during auth:', error)
          let errorMessage = 'Authentication failed'

          if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
            errorMessage = 'Unable to connect to auth server. Please check your network.'
          } else if (error instanceof Error) {
            errorMessage = `Network error: ${error.message}`
          } else {
            errorMessage = 'An unexpected error occurred during authentication'
          }

          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
            token: null
          })
          return false
        }
      },

      logout: () => {
        set({
          isAuthenticated: false,
          token: null,
          error: null
        })
      },

      checkAuth: async () => {
        const state = get()
        const { token, lastAuthCheck, isCheckingAuth, isAuthenticated } = state

        // If already checking, return current auth state
        if (isCheckingAuth) {
          return isAuthenticated
        }

        // If no token, not authenticated
        if (!token) {
          return false
        }

        // If we checked recently (within 30 seconds) and are authenticated, skip
        const now = Date.now()
        if (isAuthenticated && lastAuthCheck && (now - lastAuthCheck) < 30000) {
          return true
        }

        set({ isCheckingAuth: true })

        try {
          const authScheme = getAuthScheme(state.loginType)
          // Verify token via remote auth endpoint (agent.fkxiaoyu.com/auth)
          const valid = await verifyTokenRemote(token, authScheme)

          if (valid) {
            set({
              isAuthenticated: true,
              lastAuthCheck: now,
              isCheckingAuth: false
            })
            return true
          } else {
            set({
              isAuthenticated: false,
              token: null,
              lastAuthCheck: null,
              isCheckingAuth: false
            })
            return false
          }
        } catch (error) {
          console.error('checkAuth error:', error)
          set({
            isAuthenticated: false,
            token: null,
            lastAuthCheck: null,
            isCheckingAuth: false
          })
          return false
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        loginType: state.loginType,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      }
    }
  )
)
